import { useEffect, useState } from "react";

// Phones get the read-only layout; authoring stays on desktop. Below 700 CSS px is every phone
// in portrait (and small phones in landscape), and no window anyone authors forecasts in.
export const PHONE_MAX_WIDTH = 700;
const QUERY = `(max-width: ${PHONE_MAX_WIDTH - 0.02}px)`;

// "Use the desktop layout" sets this for the tab, so someone on a phone can still reach the
// full app. Per tab (sessionStorage), so a fresh visit lands on the phone layout again.
export const DESKTOP_OVERRIDE_KEY = "sayer-desktop-layout";
const OVERRIDE_EVENT = "sayer-layout-override";
// Holds the switch for this page load when storage is blocked.
let forcedDesktop = false;

function readOverride() {
  if (forcedDesktop) return true;
  try {
    return globalThis.sessionStorage?.getItem(DESKTOP_OVERRIDE_KEY) === "1";
  } catch {
    return false;
  }
}

function narrow() {
  try {
    return !!globalThis.matchMedia?.(QUERY).matches;
  } catch {
    return false;
  }
}

/** Switches this tab to the desktop layout (the phone layout's escape hatch). */
export function switchToDesktopLayout() {
  forcedDesktop = true;
  try {
    globalThis.sessionStorage?.setItem(DESKTOP_OVERRIDE_KEY, "1");
  } catch {
    // Storage blocked: `forcedDesktop` keeps the switch until the next reload.
  }
  globalThis.dispatchEvent?.(new Event(OVERRIDE_EVENT));
}

const isPhoneNow = () => narrow() && !readOverride();

/**
 * True while the viewport is narrower than PHONE_MAX_WIDTH CSS px (live, via matchMedia),
 * unless this tab asked for the desktop layout.
 * @returns {boolean}
 */
export function useIsPhone() {
  const [isPhone, setIsPhone] = useState(isPhoneNow);
  useEffect(() => {
    const update = () => setIsPhone(isPhoneNow());
    const mql = globalThis.matchMedia?.(QUERY);
    mql?.addEventListener?.("change", update);
    globalThis.addEventListener?.(OVERRIDE_EVENT, update);
    update();
    return () => {
      mql?.removeEventListener?.("change", update);
      globalThis.removeEventListener?.(OVERRIDE_EVENT, update);
    };
  }, []);
  return isPhone;
}
